<template>
    <div class="page">
        <!-- Loading State -->
        <v-container v-if="loading" class="text-center py-12">
            <v-progress-circular indeterminate color="avem" size="64" width="6"></v-progress-circular>
            <div class="mt-4 text-subtitle-1 text-grey-darken-1">{{ terms.loading_term }}...</div>
        </v-container>

        <!-- Invalid / Error State -->
        <v-container v-else-if="hasError || !order.number" class="py-12">
            <h1 class="text-center">{{terms.order_invalid_details}}</h1>
        </v-container>

        <!-- Order Details Content -->
        <v-container class="order-form" fluid density="compact" v-else>
            <!-- Thank you -->
            <section>
                <div class="section-title">
                    <h4>{{ terms.order_confirmation }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <h1 class="text-center">{{terms.order_message_success}}</h1>
                        <h2 class="text-center">{{terms.number}}: {{order.number}}</h2>
                    </v-col>
                </v-row>
            </section>

            <!-- Product amount -->
            <section>
                <div class="section-title">
                    <h4>{{ terms.order_section_product_amount }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <v-row class="text-center">
                            <v-col cols="3"></v-col>
                            <v-col v-for="item in order.items" :key="index">
                                <v-img :src="item.imageSrc" class="product-image" style="height: 40px;"></v-img>
                                {{ item.quantity }} {{terms.items}}
                            </v-col>
                            <v-col cols="3"></v-col>
                        </v-row>
                        <div><b>{{terms.price}}: </b>{{order.priceEuro}}{{terms.currency_euro}} | {{order.priceLeva}}{{terms.currency_leva}}</div>
                        <!--Removed Support
                            <div v-if="order.discount < 0">{{terms.discount}}: {{order.discount}} {{terms.currency_leva}}</div>
                        -->
                        <div v-if="order.deliveryCostEuro > 0"><b>{{terms.delivery_cost}}: </b>{{order.deliveryCostEuro}}{{terms.currency_euro}} | {{order.deliveryCostLeva}}{{terms.currency_leva}}</div>
                        <div v-if="order.deliveryCostEuro == 0"><b>{{terms.delivery_cost}}: </b>{{terms.no_payment}}</div>
                        <h1 class="text-center"><b>{{terms.total}}: </b>{{order.priceTotalEuro}}{{terms.currency_euro}} | {{order.priceTotalLeva}}{{terms.currency_leva}}</h1>
                    </v-col>
                </v-row>
            </section>

            <!-- Personal Info -->
            <section>
                <div class="section-title">
                    <h4>{{ terms.order_section_personal_info }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <div><strong>{{terms.order_first_name}}: </strong>{{order.firstName}}</div>
                        <div><strong>{{terms.order_last_name}}: </strong>{{order.lastName}}</div>
                        <div><strong>{{terms.order_phone}}: </strong>{{order.phone}}</div>
                        <div><strong>{{terms.order_email}}: </strong>{{order.email}}</div>
                    </v-col>
                </v-row>
            </section>

            <!-- Delivery Option -->
            <section>
                <div class="section-title">
                    <h4>{{ terms.order_section_delivery_option }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <h3>{{order.deliveryCompanyName}} {{order.deliveryToName}}</h3>
                        <div v-if="order.deliveryTo === 1">
                            <div><strong>{{terms.order_site}}: </strong>{{order.cityName}}</div>
                            <div><strong>{{terms.order_street_name}}: </strong>{{order.streetName}}</div>
                            <div><strong>{{terms.order_street_number}}: </strong>{{order.streetNumber}}</div>
                            <div v-if="order.entrence != null"><strong>{{terms.order_entrence}}: </strong>{{order.entrence}}</div>
                            <div v-if="order.floor != null"><strong>{{terms.order_floor}}: </strong>{{order.floor}}</div>
                            <div v-if="order.apartmentNumber != null"><strong>{{terms.order_apartment_number}}: </strong>{{order.apartmentNumber}}</div>
                        </div>
                        <div v-if="order.deliveryTo === 2">
                            <div><strong>{{terms.order_site}}: </strong>{{order.cityName}}</div>
                            <div><strong>{{terms.order_office}}: </strong>{{order.officeName}}</div>
                        </div>
                    </v-col>
                </v-row>
            </section>

            <!-- Payment Option -->
            <section>
                <div class="section-title">
                    <h4>{{ terms.order_section_payment_option }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <div class="d-inline-flex align-center ga-2 justify-center py-2 px-4 rounded-lg border my-1" style="background-color: #fafafa;">
                            <v-icon color="avem" size="26">{{ getPaymentIcon(order.paymentWith) }}</v-icon>
                            <h3 class="mb-0 font-weight-bold">{{order.paymentWithName}}</h3>
                        </div>
                        <div v-if="order.paymentWith === 2">
                            <div><strong>{{terms.reciever}}: </strong>{{terms.company_name}}</div>
                            <div><strong>{{terms.iban}}: </strong>{{terms.company_iban}}</div>
                            <div><strong>{{terms.bic}}: </strong>{{terms.company_bic}}</div>
                            <div><strong>{{terms.reason}}: </strong>{{order.number}}</div>
                        </div>
                        <!-- Maybe can be active at later point
                        <div><strong>{{terms.order_include_fiscal_receipt}}: </strong>                            
                            <v-icon v-if="order.includeFiscalReceipt">mdi-checkbox-marked</v-icon>
                            <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
                        </div>-->
                    </v-col>
                </v-row>
            </section>

            <!-- Invoice Details -->
            <section v-if="order.includeInvoice">
                <div class="section-title">
                    <h4>{{ terms.order_section_invoice_option }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <div><strong>{{terms.order_invoice_company_name}}: </strong>{{order.invoiceCompanyName}}</div>
                        <div><strong>{{terms.order_invoice_bulstat}}: </strong>{{order.invoiceBulstat}}</div>
                        <div><strong>{{terms.order_invoice_mol}}: </strong>{{order.invoiceMol}}</div>
                        <div><strong>{{terms.order_site}}: </strong>{{order.invoiceCity}}</div>
                        <div><strong>{{terms.order_address}}: </strong>{{order.invoiceAddress}}</div>
                        <div><strong>{{terms.order_invoice_dds}}: </strong>                            
                            <v-icon v-if="order.invoiceRegisterByDDS">mdi-checkbox-marked</v-icon>
                            <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
                        </div>
                    </v-col>
                </v-row>
            </section>

            <!-- Additional Options -->
            <section v-if="order.additionalInstructions">
                <div class="section-title">
                    <h4>{{ terms.order_section_additional_option }}</h4>
                </div>
                <v-row>
                    <v-col cols="12" class="text-center justify-center">
                        <div><strong>{{terms.order_additional_instructions}}: </strong>{{order.additionalInstructions}}</div>
                    </v-col>
                </v-row>
            </section>            
        </v-container>
    </div>
</template>

<script setup>
    import { computed, ref, inject, onBeforeMount  } from "vue";
    import { useStore } from "vuex";
    import { useRoute } from 'vue-router';
    import LZString from 'lz-string';
    import find from 'lodash/find';

    const axios = inject('axios');
    const store = useStore();
    const route = useRoute();
    const terms = computed(() => store.state.terms);
    const order = ref({});
    const loading = ref(true);
    const hasError = ref(false);
    
    const DELIVERY_COMPANY_SPEEDY = 1;
    const DELIVERY_COMPANY_ECONT = 2;
    const DELIVERY_TO_ADDRESS = 1;
    const DELIVERY_TO_OFFICE = 2;
    
    const deliveryCompanies = ref([
        { text: terms.value.delivery_company_speedy, value: DELIVERY_COMPANY_SPEEDY, logoPath: "/img/speedy_logo.png" },
        { text: terms.value.delivery_company_econt, value: DELIVERY_COMPANY_ECONT, logoPath: "/img/econt_logo.png" },
    ]);
    const deliveryTypes = ref([
        { text: terms.value.order_delivery_to_address, value: DELIVERY_TO_ADDRESS },
        { text: terms.value.order_delivery_to_office, value: DELIVERY_TO_OFFICE },
    ]);
    const paymentTypes = ref([
        { text: terms.value.order_payment_cash_on_delivery, value: 1, disabled: false },
        { text: terms.value.order_payment_bank_transfer, value: 2, disabled: false },
        { text: terms.value.order_payment_stripe, value: 3, disabled: false },
    ]); 
    
    onBeforeMount(async () => {
        try {
            loading.value = true;
            if (route.query.session_id) {
                try {
                    const verifyRes = await axios.get(`/stripe/verify-session/${route.query.session_id}`);
                    if (verifyRes.data?.paid) {
                        store.commit('clearCart');
                        sessionStorage.removeItem('avem_order_form_data');
                    }
                } catch (err) {
                    console.error("Session verification error", err);
                }
            }
            if (!route.query.order) {
                hasError.value = true;
                return;
            }
            // Decode and parse the order object    
            const orderParam = JSON.parse(LZString.decompressFromEncodedURIComponent(route.query.order));
            if (!orderParam || !orderParam.number || !orderParam.uid) {
                hasError.value = true;
                return;
            }
            const orderResponse = await axios.get(`/order/details/${orderParam.number}/${orderParam.uid}`);
            if (!orderResponse || !orderResponse.data || !orderResponse.data.number) {
                hasError.value = true;
                return;
            }
            const orderDetails = {
                ...orderResponse.data,
                deliveryToName: find(deliveryTypes.value, (dt) => dt.value === orderResponse.data.deliveryTo)?.text || '',
                paymentWithName: find(paymentTypes.value, (pt) => pt.value === orderResponse.data.paymentWith)?.text || terms.value.order_payment_stripe,
                deliveryCompanyName: find(deliveryCompanies.value, (pt) => pt.value === orderResponse.data.deliveryCompany)?.text || '',
            };
            order.value = orderDetails;
            if (route.query.session_id && orderDetails.paymentStatus === 'paid') {
                store.commit('clearCart');
                sessionStorage.removeItem('avem_order_form_data');
            }
        } catch (err) {
            console.error("Failed to load order details", err);
            hasError.value = true;
        } finally {
            loading.value = false;
        }
    });

    const getPaymentIcon = (paymentWith) => {
        switch (paymentWith) {
            case 1: return 'mdi-cash-multiple';
            case 2: return 'mdi-bank-transfer';
            case 3: return 'mdi-credit-card-outline';
            default: return 'mdi-credit-card-outline';
        }
    };
</script>

<style scoped>
    section {
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
    }

    .section-title {
        padding: 10px 0px;
    }

    ::v-deep(label.v-label.v-label--clickable) {
        padding-left: 6px !important;
        padding-right: 6px !important;
    }
</style>